import React from 'react';
import { Link } from '@remix-run/react';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import { SectionHeader } from './FeatureGrid';
import { ScrollReveal } from '~/components/motion/ScrollReveal';
import { fadeRight } from '~/lib/animations';
import { useAnimationState } from '~/lib/motion-utils';

// This would normally come from your CMS or database
interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  readingTime: number;
  category: string;
  tags: string[];
}

interface LatestNewsProps {
  posts: BlogPost[];
}

function BlogCard({ post, index = 0 }: { post: BlogPost; index?: number }) {
  const { getVariants } = useAnimationState();

  return (
    <Link to={`/blog/${post.slug}`} className="h-full">
      <motion.div
        className="opacity-0 h-full"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={getVariants({
          ...fadeRight,
          visible: {
            ...fadeRight.visible,
            transition: {
              ...(typeof fadeRight.visible === 'object' && 'transition' in fadeRight.visible
                ? fadeRight.visible.transition
                : {}),
              delay: index * 0.12, // 120ms stagger
            },
          },
        })}
        whileHover={{ y: -6, transition: { duration: 0.3 } }}
      >
        <Card className="group cursor-pointer hover:shadow-xl hover:border-primary/30 h-full overflow-hidden flex flex-col transition-all duration-300">
          {/* Featured Image */}
          <div className="aspect-video overflow-hidden rounded-t-lg">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
          </div>

          <CardHeader className="pb-3">
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="text-lumora-gold font-medium">{post.category}</span>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{new Date(post.date).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{post.readingTime} min read</span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-1 flex flex-col">
            <h3 className="text-xl font-bold mb-2 group-hover:text-lumora-purple transition-colors">
              {post.title}
            </h3>
            <p className="text-gray-600 line-clamp-2 mb-4 flex-1">{post.excerpt}</p>
            <div className="flex items-center text-lumora-purple font-medium text-sm">
              Read more
              <motion.div
                className="ml-1"
                animate={{ x: 0 }}
                whileHover={{ x: 4 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <ArrowRight className="h-4 w-4" />
              </motion.div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </Link>
  );
}

export function LatestNews({ posts }: LatestNewsProps) {
  return (
    <section className="py-20 bg-gradient-to-b from-lumora-darker to-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal pattern="fadeUp" immediate className="mb-6 md:mb-10">
          <div className="flex justify-between items-center">
            <SectionHeader
              title="Blog"
              subtitle=""
              centered={false}
            />
            <Link
              to="/blog"
              className="text-lumora-gold hover:text-lumora-gold/80 font-medium flex items-center transition-colors"
            >
              View All Posts
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-3 gap-6">
          {posts.slice(0, 3).map((post, index) => (
            <BlogCard key={post.slug} post={post} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}